/**
 * placementAnchor.ts — PLAT-10921 (epic decisions E4 + E7).
 *
 * The two directions between a `selection`-placed graphic's anchored words and
 * its timeline range.
 *
 * **The words are the source of truth; the time range is derived from them**
 * (E4). Every other placement goes the other way, time → words.
 *
 * Both directions go through `findOverlayRanges`, the same function that draws
 * the transcript underline, so the snapping semantics are identical by
 * construction rather than by agreement: deleted words (`ts == null`) never open
 * or extend a range, endpoints snap inward to the nearest surviving word, and
 * deleted words bracketed by survivors stay inside the contiguous range.
 */

import { buildOverlayMarker } from '@src/utility/overlayMarker';
import { findOverlayRanges } from '@src/utility/transcriptQuoteUtils';

import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * The parts of a transcript element this module (and `findOverlayRanges`) read.
 *
 * Structural rather than a concrete type because the two callers hand over
 * different token families — `TranscriptElement` from the transcript surfaces,
 * `ActiveTranscriptTokenWithClipSegmentAttributes` from
 * `VideoEditor.getFullTranscript()` — and `findOverlayRanges` already reads all
 * three fields defensively. Same approach as `useAITextBaseText`.
 */
export interface AnchorTranscriptElement {
  ts?: number | null;
  end_ts?: number | null;
  initial_index?: number;
}

export interface TranscriptAnchor {
  startInitialIndex: number;
  endInitialIndex: number;
}

export interface AnchorTimeRange {
  time: number;
  endTime: number;
}

/** A synthetic marker id — `findOverlayRanges` only echoes it back. */
const PROBE_ID = 'placement-anchor-probe';

function readTs(
  element: AnchorTranscriptElement | undefined,
  key: 'ts' | 'end_ts',
): number | null {
  const value = element?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * The timeline range a `selection` graphic's anchored words currently occupy.
 *
 * `null` when the anchor maps to no surviving word — the whole passage was cut.
 * Callers must treat that as "leave the graphic alone": snapping it to a
 * zero-length range at the cut point would be worse than letting it sit.
 */
export function resolveAnchorTimeRange(
  elements: readonly AnchorTranscriptElement[],
  anchor: TranscriptAnchor,
): AnchorTimeRange | null {
  const [range] = findOverlayRanges(
    [
      buildOverlayMarker({
        id: PROBE_ID,
        // A time window is required by the marker shape but unread: an anchored
        // marker maps by `initial_index` and never consults its own window.
        time: 0,
        endTime: 0,
        anchor,
      }),
    ],
    elements as unknown as TranscriptElement[],
  );
  if (!range) return null;

  const startTs = readTs(elements[range.start], 'ts');
  const endTs =
    readTs(elements[range.end], 'end_ts') ?? readTs(elements[range.end], 'ts');
  if (startTs === null || endTs === null || endTs <= startTs) return null;

  return { time: startTs, endTime: endTs };
}

/**
 * Narrow a selected array range to the anchor it can safely be stored as.
 *
 * A `transcriptAnchor` is a **source-index window**, but a selection is an
 * **array range** — and the array is ordered by timeline time. While the two
 * orders agree the window is just `[initial_index(start), initial_index(end)]`.
 * Once a section has been reordered they diverge, and that naive window is
 * wrong in one of two ways: inverted (the later array position has the smaller
 * source index, so it resolves to nothing), or silently swallowing an entire
 * section that sits between the endpoints in source order but elsewhere in the
 * array.
 *
 * So the clamp is *defined by the resolver*: build the naive window, ask
 * `findOverlayRanges` which run it resolves to (the run holding the selection's
 * first word), and keep only the part of that run the creator actually
 * selected. A selection that never crosses a reorder seam resolves to itself and
 * this is the identity — which is why every capture site can call it
 * unconditionally.
 *
 * `null` when the boundary words carry no `initial_index`; callers then store no
 * anchor and fall back to the time window, exactly as they did before.
 */
export function clampAnchorToSelectionRun(
  elements: readonly AnchorTranscriptElement[],
  selection: { startIndex: number; endIndex: number },
): TranscriptAnchor | null {
  const { startIndex, endIndex } = selection;
  const startInitialIndex = elements[startIndex]?.initial_index;
  const endInitialIndex = elements[endIndex]?.initial_index;
  if (
    typeof startInitialIndex !== 'number' ||
    typeof endInitialIndex !== 'number'
  ) {
    return null;
  }

  const [run] = findOverlayRanges(
    [
      buildOverlayMarker({
        id: PROBE_ID,
        time: 0,
        endTime: 0,
        anchor: { startInitialIndex, endInitialIndex },
      }),
    ],
    elements as unknown as TranscriptElement[],
  );
  if (!run) return null;

  // The run can reach past the selection on either side — a word outside it may
  // carry a source index that falls inside the window. Only what was selected
  // may be anchored.
  const start = Math.max(run.start, startIndex);
  const end = Math.min(run.end, endIndex);
  if (end < start) return null;

  const clampedStart = elements[start]?.initial_index;
  const clampedEnd = elements[end]?.initial_index;
  if (typeof clampedStart !== 'number' || typeof clampedEnd !== 'number') {
    return null;
  }
  return { startInitialIndex: clampedStart, endInitialIndex: clampedEnd };
}

/**
 * The words a timeline range now covers — the **Rebind** inverse (E7).
 *
 * When the creator drags a `selection` graphic's edge onto a different phrase,
 * storing only the new time would let the very next reconcile drag it back:
 * the binding still points at the old words, and the words are the source. So
 * the anchor is rewritten to whatever the new range covers, and the invariant
 * holds again — the edge equals its reference, the reference is just a different
 * passage now.
 *
 * `null` when the range covers no surviving word; the caller keeps the existing
 * anchor rather than erasing it.
 */
export function resolveTimeRangeAnchor(
  elements: readonly AnchorTranscriptElement[],
  range: AnchorTimeRange,
): TranscriptAnchor | null {
  const [mapped] = findOverlayRanges(
    // No `anchor` — this is the time-window path, which is exactly the mapping
    // we want to invert.
    [
      buildOverlayMarker({
        id: PROBE_ID,
        time: range.time,
        endTime: range.endTime,
      }),
    ],
    elements as unknown as TranscriptElement[],
  );
  if (!mapped) return null;

  // Through the clamp, not straight off the endpoints: the time window maps to a
  // contiguous ARRAY range, and after a section reorder that range's boundary
  // words can bracket a source-index window covering words it does not contain.
  // Storing that unclamped would hand the next reconcile a range wider than the
  // one the creator just dragged.
  return clampAnchorToSelectionRun(elements, {
    startIndex: mapped.start,
    endIndex: mapped.end,
  });
}
